"use client";

import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { useSnackbar } from "notistack";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { instantBookingModel } from "@/src/shared/features/booking";
import {
  dateAdd,
  formatMonthDateFullYear,
  formatYearDate,
} from "@/src/shared/utils/timeFunctionsModel";
import { yupResolver } from "@hookform/resolvers/yup";
import { AnimatePresence, motion } from "framer-motion";
import DatePicker from "@/src/shared/components/DatePicker";
import {
  GuestsModal,
  type IFilter,
  BookingMessage,
} from "@/src/shared/features/search";
import type { IListing } from "@/src/shared/types/listing";
import SubmitButton from "@/src/shared/components/SubmitButton";
import PriceDisplay from "@/src/features/listing/components/PriceDisplay";
import {
  type BookingFormValues,
  type IBookingFormType,
  bookingFormSubmitSchema,
} from "@/src/shared/types/schemaType";
import { dropdownVariants } from "@/src/shared/constants/animation";
import ErrorMessage from "@/src/shared/components/atoms/ErrorMessage";
import {
  formatGuestText,
  getFormattedDateRange,
} from "@/src/shared/utils/global";
import { useBookingRequest } from "@/src/features/listing/hooks/useBookingRequest";
import { useAuth } from "@/src/core/auth/useAuth";
import type { BlockedDate } from "@/src/shared/types/blockedDate";
import useAnalytics from "@/src/lib/useAnalytics";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

interface IBookNowCardProps {
  singleListingData?: IListing;
  location?: string | string[];
  placeName?: string | string[];
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  handleUpdateQuery: () => void;
  blockedDates?: BlockedDate[];
  checkoutOnlyDates?: string[]; // newly added
}
interface IDropdownPosition {
  top: number;
  left: number;
  width: number;
}

export default function BookNowCard({
  singleListingData,
  filters,
  setFilters,
  handleUpdateQuery,
  blockedDates,
  checkoutOnlyDates = [],
}: IBookNowCardProps) {
  const { requireAuth, user_profile } = useAuth();
  const router = useAppRouter();
  const requestBooking = useBookingRequest();
  const { message } = router.query;
  const { enqueueSnackbar } = useSnackbar();
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showGuestPicker, setShowGuestPicker] = useState<boolean>(false);
  const dateFieldRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const guestFieldRef = useRef<HTMLButtonElement>(null);
  const [datePos, setDatePos] = useState<IDropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
  });
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [pendingBookingData, setPendingBookingData] =
    useState<IBookingFormType | null>(null);
  const analytics = useAnalytics();

  const isInstantBooking = instantBookingModel(singleListingData);

  const {
    register,
    handleSubmit,
    trigger,
    setError,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<BookingFormValues>({
    resolver: yupResolver(bookingFormSubmitSchema, {
      context: { $isMessageRequired: true },
    }),
    reValidateMode: "onChange",
    defaultValues: {
      message: (message as string) ?? "",
      guest: String(filters?.guests?.adults || 1),
      dates: getFormattedDateRange(filters?.checkIn, filters?.checkOut),
    },
  });

  // Update only specific fields when filters change to preserve textarea content
  useEffect(() => {
    setValue("guest", String(filters?.guests?.adults || 1));
    setValue(
      "dates",
      getFormattedDateRange(filters?.checkIn, filters?.checkOut),
    );
    // Don't reset the message field to preserve user input
  }, [filters?.guests?.adults, filters?.checkIn, filters?.checkOut, setValue]);

  // Separate effect to handle initial message from query params
  useEffect(() => {
    if (message) {
      setValue("message", message as string);
    }
  }, [message, setValue]);

  // Auto-submit booking after login success
  useEffect(() => {
    if (user_profile && pendingBookingData) {
      // User just logged in and we have pending booking data
      void handleBookingSubmission(pendingBookingData);
      setPendingBookingData(null);
    }
  }, [user_profile, pendingBookingData]);

  const handleBookingSubmission = async (data: IBookingFormType) => {
    if (!singleListingData) return;
    setIsBookingLoading(true);

    const fromDate = filters.checkIn ? formatYearDate(filters.checkIn) : null;
    const toDate = filters.checkOut
      ? formatYearDate(filters.checkOut)
      : fromDate
        ? formatYearDate(dateAdd(fromDate, 1))
        : null;

    if (fromDate && toDate) {
      const payload = {
        message: data.message,
        from: fromDate,
        to: toDate,
        listing_id: singleListingData?.id,
        guests: Number(filters.guests.adults),
        child: Number(filters.guests.children),
        infant: Number(filters.guests.infants),
        os_platform: 3,
      };

      try {
        analytics.add_to_cart({
          item: singleListingData,
          value: singleListingData?.price,
          currency: "BDT",
          params: {
            booking_dates: `${payload.from} to ${payload.to}`,
            guests:
              payload.guests + (payload.child || 0) + (payload.infant || 0),
          },
        });
      } catch (_error) {}

      await requestBooking(payload, {
        hostId: singleListingData.host.id,
        guestId: user_profile?.id,
        instant: isInstantBooking,
      });
      setIsBookingLoading(false);
    } else {
      enqueueSnackbar("Please select check-in and check-out dates", {
        variant: "error",
      });
      setIsBookingLoading(false);
    }
  };

  const onSubmit = async (data: IBookingFormType) => {
    // First validate the form data
    if (!isInstantBooking && (!data.message || data.message.trim() === "")) {
      setError(
        "message",
        { message: "Please write something" },
        { shouldFocus: true },
      );
      return;
    }

    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    setFilters((prevState) => {
      return {
        ...prevState,
        message: data.message,
      };
    });

    // Use requireAuth to ensure user is authenticated before proceeding
    requireAuth(
      () => {
        // This callback runs only if user is already authenticated
        void handleBookingSubmission(data);
      },
      () => {
        // This callback runs when login modal opens (user not authenticated)
        // Store the booking data to submit after login
        setPendingBookingData(data);
      },
    );
  };

  const getFormattedGuestText = useCallback(() => {
    return formatGuestText(filters.guests);
  }, [filters]);

  // viewport-aware placement for md+ screens
  const calcPlacement = useCallback(
    (
      triggerEl: HTMLElement | null,
      preferredWidth = 650,
    ): IDropdownPosition => {
      if (!triggerEl) return { top: 0, left: 0, width: 0 };
      const rect = triggerEl.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 16; // viewport margin
      const width = Math.min(preferredWidth, vw - margin * 2);

      // center under the trigger, then clamp to viewport
      let left = rect.left + rect.width / 2 - width / 2;
      left = Math.max(margin, Math.min(left, vw - margin - width));

      const gap = 8;
      let top = rect.bottom + gap;
      // ensure it doesn't go below viewport; if it does, try above
      const estimatedHeight = 420; // rough height for your date/guest popups on md+
      if (
        top + estimatedHeight > vh - margin &&
        rect.top - gap - estimatedHeight > margin
      ) {
        top = rect.top - gap - estimatedHeight; // place above if more space
      }

      return { top, left, width };
    },
    [],
  );

  const updatePositions = useCallback(() => {
    if (typeof window === "undefined" || window.innerWidth < 768) return;
    if (showDatePicker && dateFieldRef.current) {
      const placement = calcPlacement(dateFieldRef.current, 550);
      const card = cardRef.current?.getBoundingClientRect();
      setDatePos(
        card ? { ...placement, left: card.left, width: card.width } : placement,
      );
    }
  }, [showDatePicker, calcPlacement]);

  useIsomorphicLayoutEffect(() => {
    updatePositions();
    // reflow on these events
    const onWin = () => updatePositions();
    if (showDatePicker || showGuestPicker) {
      window.addEventListener("resize", onWin);
      window.addEventListener("scroll", onWin, { passive: true });
    }
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin);
    };
  }, [updatePositions, showDatePicker, showGuestPicker]);

  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Element;
      if (
        showDatePicker &&
        dateFieldRef.current &&
        !dateFieldRef.current.contains(target)
      ) {
        const inPopup = target.closest && target.closest("[data-popup='date']");
        if (!inPopup) setShowDatePicker(false);
      }
      if (
        showGuestPicker &&
        guestFieldRef.current &&
        !guestFieldRef.current.contains(target)
      ) {
        const inPopup =
          target.closest && target.closest("[data-popup='guest']");
        if (!inPopup) setShowGuestPicker(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowDatePicker(false);
        setShowGuestPicker(false);
      }
    };
    if (showDatePicker || showGuestPicker) {
      document.addEventListener("mousedown", handleDocClick);
      document.addEventListener("keydown", handleEsc);
    }
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showDatePicker, showGuestPicker]);

  // Close other dropdown when one opens
  const handleDatePickerToggle = useCallback(() => {
    setShowDatePicker((prev) => {
      const next = !prev;
      if (next) setShowGuestPicker(false);
      return next;
    });
  }, []);

  const handleGuestPickerToggle = useCallback(() => {
    setShowGuestPicker((prev) => {
      const next = !prev;
      if (next) setShowDatePicker(false);
      return next;
    });
  }, []);

  const getTotalGuests = useCallback(() => {
    const formattedText = getFormattedGuestText();
    return formattedText || "Select Guests";
  }, [getFormattedGuestText]);

  const updateGuestCount = useCallback(
    (type: keyof IFilter["guests"], op: "add" | "subtract") => {
      const currentValue = filters.guests[type] ?? 0;
      const minValue = type === "adults" ? 1 : 0;
      const newValue =
        op === "add" ? currentValue + 1 : Math.max(currentValue - 1, minValue);

      setFilters((f: IFilter) => {
        return {
          ...f,
          guests: {
            ...f.guests,
            [type]: newValue,
          },
        };
      });
    },
    [filters, setFilters],
  );

  return (
    <div className="relative" key={`form-${isInstantBooking}`}>
      {" "}
      {/* Add key to force re-mount on isInstantBooking change */}
      <div
        ref={cardRef}
        className="p-6 bg-white rounded-2xl shadow-[0px_8px_40px_rgba(0,0,0,0.04)]"
      >
        <PriceDisplay filters={filters} variant="web" />
        <div className="space-y-6" id="form-fields">
          {/* Date Range Field */}
          <div className="space-y-2">
            <button
              ref={dateFieldRef}
              type="button"
              onClick={() => {
                handleDatePickerToggle();
                setTimeout(updatePositions, 0);
              }}
              className={`h-[68px] w-full border rounded-lg transition-all duration-200 ${
                showDatePicker
                  ? " shadow-md border-red-300"
                  : "border-gray-300 hover:border-gray-400"
              } ${errors.dates ? "border-red-300 ring-2 ring-red-100" : ""} text-left`}
            >
              <div className="grid grid-cols-2 h-full">
                {/* Check-in */}
                <div className="flex items-center px-3 border-r border-gray-300">
                  <div className="p-3">
                    <img
                      src="/images/icons/calendar.svg"
                      alt="Calendar"
                      className="w-5 h-5"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink-gray">
                      Check In
                    </div>
                    <div className="text-sm font-medium text-ink-black truncate">
                      {filters.checkIn
                        ? formatMonthDateFullYear(filters.checkIn)
                        : "Select Date"}
                    </div>
                  </div>
                </div>
                {/* Check-out */}
                <div className="flex items-center px-3">
                  <div className="p-3">
                    <img
                      src="/images/icons/calendar.svg"
                      alt="Calendar"
                      className="w-5 h-5"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink-gray">
                      Check Out
                    </div>
                    <div className="text-sm font-medium text-ink-black truncate">
                      {filters.checkOut
                        ? formatMonthDateFullYear(filters.checkOut)
                        : "Select Date"}
                    </div>
                  </div>
                </div>
              </div>
            </button>
            <ErrorMessage message={errors.dates?.message} />
          </div>

          {/* Guest Field */}
          <div className="space-y-2">
            <button
              ref={guestFieldRef}
              type="button"
              onClick={() => {
                handleGuestPickerToggle();
                setTimeout(updatePositions, 0);
              }}
              className={`h-[68px] w-full border rounded-lg transition-all duration-200 flex items-center ${
                showGuestPicker
                  ? "border-red-300 shadow-md"
                  : "border-gray-300 hover:border-gray-400"
              } ${errors.guest ? "border-red-300 ring-2 ring-red-100" : ""} text-left`}
            >
              <div className="p-3">
                <img
                  src="/images/icons/profile.svg"
                  alt="Profile"
                  className="w-5 h-5"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink-gray">Guests</div>
                <div className="text-sm font-medium text-ink-black truncate">
                  {getTotalGuests()}
                </div>
              </div>
              <div className="p-3">
                <img
                  src="/images/icons/Vector.svg"
                  alt="Arrow"
                  className={`w-4 h-4 transition-transform duration-200 ${showGuestPicker ? "rotate-180" : ""}`}
                />
              </div>
            </button>
            <ErrorMessage message={errors.guest?.message} />
          </div>

          {!isInstantBooking && (
            <div>
              <BookingMessage
                label="Ask anything you want"
                aria-invalid={!!errors?.message}
                aria-describedby={
                  errors?.message?.message ? "message-error" : undefined
                }
                error={errors}
                register={register}
              />
            </div>
          )}
          {/* Submit */}
          <SubmitButton
            label={
              instantBookingModel(singleListingData)
                ? "BOOK NOW"
                : "SEND BOOKING REQUEST"
            }
            isLoading={isSubmitting || isBookingLoading}
            type="button"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </div>
      {/* === Floating portals for md+ (fit to viewport, center + clamp) === */}
      <AnimatePresence>
        {showDatePicker && (
          <motion.div
            key="date-portal"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={dropdownVariants}
            className="hidden md:block fixed z-[1000] "
            data-popup="date"
            style={{
              top: Math.max(8, datePos.top),
              left: datePos.left,
              width: datePos.width,
            }}
          >
            <DatePicker
              selectedDates={{
                checkIn: filters.checkIn,
                checkOut: filters.checkOut,
              }}
              onDateChange={(dates) => setFilters((f) => ({ ...f, ...dates }))}
              onConfirm={() => {
                setShowDatePicker(false);
                handleUpdateQuery();
              }}
              unableGlobalScroll={true}
              isMonthSingleView={true}
              appearance="popover"
              blockedDates={blockedDates}
              checkoutOnlyDates={checkoutOnlyDates}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showGuestPicker && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95, x: 40 }}
            animate={{
              opacity: 1,
              y: isInstantBooking ? -90 : -220,
              scale: 1,
              x: 40,
            }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-3xl shadow-xl  p-6 w-[400px]"
            data-popup="guest"
          >
            <GuestsModal
              isAppliedValidation={true}
              maxAdults={singleListingData?.max_guest}
              maxChildren={singleListingData?.max_child}
              maxInfants={singleListingData?.max_infant}
              filters={filters}
              updateGuestCount={updateGuestCount}
            />
          </motion.div>
        )}
      </AnimatePresence>
      {/* Backdrop for < md only */}
      <AnimatePresence>
        {(showDatePicker || showGuestPicker) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.2 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black z-[900] md:hidden"
            onClick={() => {
              setShowDatePicker(false);
              setShowGuestPicker(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
